import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { saveAs } from "file-saver";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { neutral } from "app/themes/colors";
import {
  setCustomMessage,
  setOpen as setSnackbarOpen
} from "components/action-snackbar/action-snackbar-slice";
import Dialog from "components/dialog";
import { usePatientPrivacy } from "hooks/use-patient-privacy.hook";
import useUserRoles from "hooks/use-user-roles.hook";
import setupLocalStorageAPI from "utils/setupLocalStorageAPI";

import KeyCap from "../key-cap.component";
import {
  type QuickLauncherScreenshotExportMethod,
  trackQuickLauncherScreenshotExported
} from "../quick-launcher-telemetry";
import {
  dismissScreenshotRequest,
  requestScreenshot,
  selectQuickLauncherScreenshotRequest
} from "../quick-launcher.slice";
import {
  PATIENT_ROUTE_PATTERN,
  SALE_ROUTE_PATTERN
} from "../use-quick-launcher-context.hook";
import capturePage, {
  ScreenshotCancelledError,
  isScreenshotSupported
} from "./capture-page.util";
import composeScreenshot, {
  LOGO_DARK_COLOR,
  LOGO_LIGHT_COLOR
} from "./compose-screenshot.util";
import {
  canCopyImages,
  canvasToPngBlob,
  copyPngToClipboard,
  screenshotFileName
} from "./export-screenshot.util";
import loadSunoLogo from "./load-suno-logo.util";
import { SCREENSHOT_BLOCKING_ATTRIBUTE } from "./screenshot-ignore.const";
import ScreenshotStylePicker from "./screenshot-style-picker.component";
import {
  DEFAULT_SCREENSHOT_STYLE,
  type ScreenshotStyle,
  findBackground,
  findPadding,
  parseScreenshotStyle
} from "./screenshot.types";

/**
 * Per-browser, like the launcher's own drag position: a preferred look is a
 * local habit, not something worth a user setting or a round trip.
 */
const styleStorage = setupLocalStorageAPI("quickLauncher.screenshotStyle");

const PREVIEW_MAX_HEIGHT = 340;

/** Mirrors `parseRouteId` in `use-quick-launcher-context.hook`. */
const isRecordId = (raw: string | undefined): boolean =>
  raw !== undefined && /^\d+$/.test(raw);

type ScreenshotStatus = "idle" | "capturing" | "ready" | "cancelled" | "error";

/**
 * The ⌘K → "Screenshot this page" flow, end to end.
 *
 * One rule governs the order: the dialog is only ever `open` once a capture
 * exists. The capture is the browser's own pixels, so there is no DOM filtering
 * to fall back on — anything painted when the frame lands is in the picture.
 * A second request flips the status back to `capturing`, which closes the
 * dialog before the next frame is grabbed, and its body carries
 * `SCREENSHOT_BLOCKING_ATTRIBUTE` so `capturePage` also waits out the exit
 * transition rather than trusting the render to have happened.
 *
 * `capturePage` is called straight from the request, with no timer in front of
 * it: `getDisplayMedia` needs the transient user activation from the keystroke
 * that opened the launcher, and a delay risks spending it.
 */
export default function QuickLauncherScreenshotDialog() {
  const dispatch = useAppDispatch();
  const request = useAppSelector(selectQuickLauncherScreenshotRequest);
  const { pathname } = useLocation();
  const { isPrivateMode } = usePatientPrivacy();
  const { isSunoAdmin } = useUserRoles();

  const [status, setStatus] = useState<ScreenshotStatus>("idle");
  const [capture, setCapture] = useState<HTMLCanvasElement | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [style, setStyle] = useState<ScreenshotStyle>(() =>
    parseScreenshotStyle(styleStorage.getData(DEFAULT_SCREENSHOT_STYLE))
  );

  // The composed PNG, kept so Copy and Download do not re-encode what the
  // preview already produced.
  const blobRef = useRef<Blob | null>(null);
  // Object URLs are revoked by hand; the preview is replaced on every style
  // change, and a multi-megabyte PNG per click adds up over one sitting.
  const previewUrlRef = useRef<string | null>(null);

  const replacePreview = useCallback((url: string | null) => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = url;
    setPreviewUrl(url);
  }, []);

  useEffect(() => () => replacePreview(null), [replacePreview]);

  const background = findBackground(style.backgroundId);
  const padding = findPadding(style.paddingId);

  // Read straight from the URL rather than from `useQuickLauncherContext`: all
  // this needs to know is "does this surface show a patient", which the path
  // answers — no patient fetch, and nothing here ever holds patient data.
  //
  // The id has to be checked for being a number, not just present: the route
  // patterns are `/patients/:patientId/*` and `.../billing/:saleId/*`, and
  // `:patientId` happily matches the literal "new". Warning about patient data
  // on the empty new-patient wizard would teach people to ignore the notice.
  const contextType = useMemo(() => {
    const sale = matchPath(SALE_ROUTE_PATTERN, pathname);
    if (sale && isRecordId(sale.params.patientId)) {
      return isRecordId(sale.params.saleId)
        ? ("sale" as const)
        : ("patient" as const);
    }
    const patient = matchPath(PATIENT_ROUTE_PATTERN, pathname);
    return patient && isRecordId(patient.params.patientId)
      ? ("patient" as const)
      : null;
  }, [pathname]);
  const isPatientSurface = contextType !== null;

  // Capture. Deliberately not on `request` itself — the object is replaced on
  // every dispatch, and keying on the nonce says "a new request", not "a new
  // reference".
  useEffect(() => {
    if (!request) return undefined;
    let cancelled = false;
    setStatus("capturing");
    setCapture(null);
    blobRef.current = null;
    replacePreview(null);

    (async () => {
      try {
        const canvas = await capturePage();
        if (cancelled) return;
        setCapture(canvas);
        setStatus("ready");
      } catch (error) {
        if (cancelled) return;
        if (error instanceof ScreenshotCancelledError) {
          // Dismissing the share picker is a decision, not a fault. No log.
          setStatus("cancelled");
          return;
        }
        console.error("Quick launcher screenshot failed:", error);
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.nonce]);

  // Compose. Re-runs on every style change; the capture is reused.
  useEffect(() => {
    if (!capture) return undefined;
    let cancelled = false;

    (async () => {
      try {
        const logo = await loadSunoLogo(
          background.isDark ? LOGO_LIGHT_COLOR : LOGO_DARK_COLOR
        );
        if (cancelled) return;
        const composed = composeScreenshot({
          capture,
          background,
          padding,
          logo
        });
        const blob = await canvasToPngBlob(composed);
        if (cancelled) return;
        blobRef.current = blob;
        replacePreview(URL.createObjectURL(blob));
      } catch (error) {
        if (cancelled) return;
        console.error("Quick launcher screenshot framing failed:", error);
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [capture, background, padding, replacePreview]);

  const persistStyle = useCallback((next: ScreenshotStyle) => {
    setStyle(next);
    styleStorage.updateData(next);
  }, []);

  const handleClose = useCallback(() => {
    setStatus("idle");
    setCapture(null);
    blobRef.current = null;
    replacePreview(null);
    dispatch(dismissScreenshotRequest());
  }, [dispatch, replacePreview]);

  const announce = useCallback(
    (message: string, duration: number) => {
      dispatch(setCustomMessage(message));
      dispatch(setSnackbarOpen(duration));
    },
    [dispatch]
  );

  const recordExport = useCallback(
    (method: QuickLauncherScreenshotExportMethod) => {
      trackQuickLauncherScreenshotExported({
        method,
        backgroundId: style.backgroundId,
        paddingId: style.paddingId,
        contextType,
        privacyMode: isPrivateMode,
        internalUser: isSunoAdmin
      });
    },
    [style, contextType, isPrivateMode, isSunoAdmin]
  );

  const handleDownload = useCallback(() => {
    const blob = blobRef.current;
    if (!blob) return;
    saveAs(blob, screenshotFileName());
    recordExport("download");
    handleClose();
  }, [recordExport, handleClose]);

  const handleCopy = useCallback(async () => {
    const blob = blobRef.current;
    if (!blob) return;
    setIsExporting(true);
    try {
      await copyPngToClipboard(blob);
      recordExport("copy");
      announce("Screenshot copied to clipboard", 3000);
      handleClose();
    } catch (error) {
      // `clipboard.write` is refused without focus or permission. Saying so is
      // the whole value here — a silent failure leaves the user pasting the
      // last thing they copied into a ticket.
      console.error("Failed to copy the screenshot:", error);
      announce("Couldn't copy the screenshot — try Download instead", 5000);
    } finally {
      setIsExporting(false);
    }
  }, [recordExport, announce, handleClose]);

  const isReady = status === "ready" && Boolean(previewUrl);
  const didNotCapture = status === "cancelled" || status === "error";
  const open = status === "ready" || didNotCapture;
  const copySupported = canCopyImages();
  const canCopyNow = isReady && !isExporting && copySupported;

  // Enter copies — the overwhelmingly common ending, and the one the launcher
  // itself trained the user to expect from ↵.
  //
  // Bound on the document rather than on the dialog body: MUI parks initial
  // focus on the Dialog's Paper, which is an ancestor of anything this
  // component renders, so a React `onKeyDown` down here would never see the
  // keystroke. The trade is that it must not steal Enter from a control that
  // already means something by it, hence the target check.
  useEffect(() => {
    if (!canCopyNow) return undefined;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Enter" || event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (
        target?.isContentEditable ||
        target?.getAttribute?.("role") === "button" ||
        ["button", "a", "input", "textarea", "select"].includes(tag ?? "")
      ) {
        // The user tabbed to Download, or to a swatch. Let it have its Enter.
        return;
      }
      event.preventDefault();
      void handleCopy();
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [canCopyNow, handleCopy]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Screenshot"
      subtitle="An exact capture of this page, ready to paste or save"
      maxWidth="md"
      minWidth={720}
      actions={
        didNotCapture ? (
          <Button
            variant="contained"
            onClick={() => dispatch(requestScreenshot())}
            data-testid="screenshot-retry"
          >
            Try again
          </Button>
        ) : (
          <Stack direction="row" alignItems="center" gap={1}>
            {canCopyNow && (
              <Stack
                direction="row"
                alignItems="center"
                gap={0.75}
                sx={{ mr: 1 }}
                data-testid="screenshot-enter-hint"
              >
                <KeyCap>↵</KeyCap>
                <Typography variant="caption" color="text.secondary">
                  to copy
                </Typography>
              </Stack>
            )}
            <Button
              variant="outlined"
              startIcon={<ContentCopyOutlinedIcon />}
              onClick={handleCopy}
              disabled={!canCopyNow}
              data-testid="screenshot-copy"
            >
              Copy
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadOutlinedIcon />}
              onClick={handleDownload}
              disabled={!isReady || isExporting}
              data-testid="screenshot-download"
            >
              Download PNG
            </Button>
          </Stack>
        )
      }
    >
      {/* The blocking attribute, not the testid, is what `capturePage` waits
          on — it keeps this dialog out of the next capture while it fades. */}
      <Stack
        gap={2}
        data-testid="quick-launcher-screenshot"
        {...{ [SCREENSHOT_BLOCKING_ATTRIBUTE]: "" }}
      >
        {status === "cancelled" ? (
          <Alert severity="info" data-testid="screenshot-cancelled">
            No screenshot was taken. Your browser asks which surface to capture
            — choose <strong>this tab</strong> and the picture appears here.
          </Alert>
        ) : status === "error" ? (
          <Alert severity="error" data-testid="screenshot-error">
            {isScreenshotSupported()
              ? "Couldn’t read a frame from this tab. Try again, or use your computer’s own screenshot tool."
              : "This browser can’t capture its own tab. Chrome and Edge can; your computer’s own screenshot tool works anywhere."}
          </Alert>
        ) : (
          <>
            {isPatientSurface &&
              (isPrivateMode ? (
                <Alert
                  severity="info"
                  variant="outlined"
                  data-testid="screenshot-privacy-notice"
                >
                  Privacy mode is on, so patient names are masked in this
                  capture.
                </Alert>
              ) : (
                <Alert
                  severity="warning"
                  variant="outlined"
                  data-testid="screenshot-phi-notice"
                >
                  This page shows patient information, and the capture includes
                  it exactly as displayed. Check the preview before you share
                  it.
                </Alert>
              ))}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: PREVIEW_MAX_HEIGHT,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${neutral[200]}`,
                // A checkerboard, so the Transparent background reads as
                // transparent rather than as white.
                backgroundColor: "common.white",
                backgroundImage: `linear-gradient(45deg, ${neutral[100]} 25%, transparent 25%, transparent 75%, ${neutral[100]} 75%), linear-gradient(45deg, ${neutral[100]} 25%, transparent 25%, transparent 75%, ${neutral[100]} 75%)`,
                backgroundSize: "16px 16px",
                backgroundPosition: "0 0, 8px 8px"
              }}
            >
              {isReady ? (
                <Box
                  component="img"
                  src={previewUrl ?? undefined}
                  alt="Screenshot preview"
                  data-testid="screenshot-preview"
                  sx={{
                    display: "block",
                    maxWidth: "100%",
                    maxHeight: PREVIEW_MAX_HEIGHT,
                    objectFit: "contain"
                  }}
                />
              ) : (
                <Stack alignItems="center" gap={1.5}>
                  <CircularProgress size={22} />
                  <Typography variant="caption" color="text.secondary">
                    Framing your screenshot…
                  </Typography>
                </Stack>
              )}
            </Box>

            <ScreenshotStylePicker
              backgroundId={style.backgroundId}
              paddingId={style.paddingId}
              disabled={!isReady}
              onBackgroundChange={(backgroundId) =>
                persistStyle({ ...style, backgroundId })
              }
              onPaddingChange={(paddingId) =>
                persistStyle({ ...style, paddingId })
              }
            />

            {!copySupported && (
              <Typography variant="caption" color="text.secondary">
                This browser can’t put images on the clipboard — use Download.
              </Typography>
            )}
          </>
        )}
      </Stack>
    </Dialog>
  );
}
